"use client";
import { Fragment } from "react";
import { usePathname } from "next/navigation";
import { Stack, Drawer, Box, Divider } from "@mui/material";
import {
  SearchRounded as SearchIcon,
  RssFeedRounded as FeedIcon
} from "@mui/icons-material";
import ModelsIcon from "@mui-symbols-material/w400/DeployedCodeFilledRounded";

import { useStore, observer } from "../../service/mobx";
import { ListItem } from "../List/ListItem";
import Sessions from "./Session";
import Plan from "./Plan";
import Developers from "./Developers";
import { useWhiteLabelling } from "../Hooks";
import ToolTip from "../Tooltip";
import Account from "./Account";

// const Account = dynamic(() => import("./Account"), { ssr: false });

function Navigation() {
  const { layout, device } = useStore();
  const isWhiteLabelled = useWhiteLabelling();
  const loading = device.measured === false;
  const links = [
    { label: "Search", href: "/search", Icon: SearchIcon, tooltip: "Search" },
    {
      label: "Feed",
      href: "/feed",
      Icon: FeedIcon,
      tooltip: "Keep up with AI"
    },
    {
      label: "Models",
      href: "/models",
      Icon: ModelsIcon,
      tooltip: "Search models"
    }
  ];

  return (
    <Drawer
      variant={device.isPhone ? "temporary" : "permanent"}
      open={layout.navOpen}
      onClose={() => layout.set.navOpen(false)}
      sx={theme => ({
        displayPrint: "none",
        display: loading || isWhiteLabelled ? "none" : undefined,
        width: layout.navOpen ? 280 : device.isPhone ? 0 : 72,
        transition: theme.transitions.create("width", {
          easing: `var(--motion-easing-emphasized${
            device.isPhone ? "" : layout.navOpen ? "Decelerate" : "Accelerate"
          })`,
          duration: layout.navOpen ? (device.isPhone ? 500 : 400) : 200
        }),
        overflowX: "hidden",
        zIndex: "calc( var(--zIndex-appBar) - 1 )",
        "& .MuiDrawer-paper": {
          px: 1,
          border: "none",
          borderRight: 2,
          borderColor: "var(--surface-dim)",
          width:
            device.isPhone === false && layout.navOpen === false ? 74 : 280,
          overflowX: "hidden",
          boxSizing: "border-box",
          backgroundImage: "none",
          bgcolor: "var(--surface-dim)",
          zIndex: "calc( var(--zIndex-appBar) - 1 )",
          transition: theme.transitions.create("width", {
            easing: `var(--motion-easing-emphasized${
              device.isPhone ? "" : layout.navOpen ? "Decelerate" : "Accelerate"
            })`,
            duration: layout.navOpen ? (device.isPhone ? 500 : 400) : 200
          })
        }
      })}
    >
      <Stack
        justifyContent="space-between"
        height="inherit"
        pb={2}
        sx={{
          "& svg, .MuiTypography-root": {
            color: "var(--secondary-color)"
          }
        }}
      >
        <Box sx={{ pt: 9 }}>
          {links.map(({ label, href, Icon, tooltip }, index) => (
            <Fragment key={index}>
              <ToolTip placement="right" title={layout.navOpen ? "" : tooltip}>
                <NavListItem line1={label} href={href} leading={{ Icon }} />
              </ToolTip>
              {index === 0 ? (
                <Divider
                  orientation="horizontal"
                  variant="middle"
                  sx={{
                    my: layout.navOpen ? 0 : 2,
                    transition: `all 0.${
                      layout.navOpen ? 4 : 2
                    }s var(--motion-easing-emphasized${
                      layout.navOpen ? "Decelerate" : "Accelerate"
                    })`
                  }}
                />
              ) : null}
            </Fragment>
          ))}
          <Sessions />
        </Box>
        <Stack useFlexGap spacing={2}>
          <Developers />
          <Plan />
          <Account />
        </Stack>
      </Stack>
    </Drawer>
  );
}

export default observer(Navigation);

export function NavListItem({ sx, href, ...props }) {
  const selected = usePathname().startsWith(href);
  const transition = "all 500ms var(--motion-easing-emphasized)";

  return (
    <ListItem
      sx={{
        transition,
        borderRadius: "var(--shape-md-round)",
        bgcolor: selected ? "var(--surface-container-highest)" : undefined,
        "& div > svg:first-of-type ": {
          transition,
          color: selected ? "var(--primary-color)" : undefined
        },
        "& span": {
          whiteSpace: "nowrap",
          color: selected ? "var(--surface-on-container)" : undefined
        },

        ...sx
      }}
      href={href}
      {...props}
    />
  );
}
